import { ChangeEvent, FC, useContext } from "react";

import {
	Box,
	Grid,
	IconButton,
	NativeSelect,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useTime } from "@/components/Application/TimeContext";
import {
	GetLanguageValue,
	Translate,
} from "@/components/Application/Translate";
import { tokens } from "@/components/Application/theme";
import { IActiveArchiveProcedures } from "@/components/Archive/ActiveArchiveProcedures";

import { TranslationContext } from "@/model/Translation/TranslationContext";

import { initialDate } from "@/service/ApplicationQueries";
import { useFetchArchiveUsers } from "@/service/ArchiveQueries";

dayjs.extend(utc);

interface ISearchArchive extends IActiveArchiveProcedures {
	SetStartDate: (date: Date) => void;
	SetEndDate: (date: Date) => void;
	SetSelectedUser: (userId: number) => void;
}

export const ArchiveProcedureSearch: FC<ISearchArchive> = (props) => {
	const { data: archiveUsers } = useFetchArchiveUsers();
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	const selectedUserFound = archiveUsers?.Users.findIndex(
		(user) => user.UserId === props.selectedUser,
	);
	const { timezone } = useTime();

	const onDateChange = (
		e: ChangeEvent<HTMLInputElement>,
		setDate: (date: Date) => void,
	) => {
		setDate(dayjs.utc(e.target.value, "YYYY-MM-DD").tz(timezone).toDate());
	};

	const onUserChange = (e: ChangeEvent<HTMLSelectElement>) => {
		props.SetSelectedUser(parseInt(e.target.value));
	};

	const translationContext = useContext(TranslationContext);

	if (selectedUserFound === -1 && props.selectedUser !== 0) {
		props.SetSelectedUser(0);
	}

	return (
		<Box sx={{ mt: 4, mb: 1, position: "relative" }}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={2}>
						<TextField
							id="startdate"
							type="date"
							label={<Translate String="Archive.CompletedProcedures.Search.StartDate">Date from</Translate>}
							fullWidth
							value={dayjs
								.utc(props.startDate)
								.tz(timezone)
								.format("YYYY-MM-DD")}
							onChange={(e) => onDateChange(e, props.SetStartDate)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid item xs={12} sm={2}>
						<TextField
							id="enddate"
							type="date"
							label={<Translate String="Archive.CompletedProcedures.Search.EndDate">Date from</Translate>}
							fullWidth
							value={dayjs.utc(props.endDate).tz(timezone).format("YYYY-MM-DD")}
							onChange={(e) => onDateChange(e, props.SetEndDate)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<NativeSelect
							id="selecteduser"
							fullWidth
							value={props.selectedUser}
							onChange={(e) => onUserChange(e)}
						>
							<option value="0">
								{
									GetLanguageValue(
										translationContext,
										"Archive.CompletedProcedures.Search.ResponsibleUser.AllUsers",
										"All users",
									).value
								}
							</option>
							{archiveUsers?.Users.map((user) => (
								<option key={user.UserId} value={user.UserId}>
									{user.UserName}
								</option>
							))}
						</NativeSelect>
					</Grid>
					<Grid item xs={12}>
						<Typography>Last months: 	</Typography>
						<Box display="flex" gap={1}>
						<IconButton
							size="small"
							sx={{ border: "1px solid", borderColor: colors.blueAccent[300], width: 50, height: 50, }}
							onClick={() =>
								props.SetStartDate(
									dayjs.utc(initialDate(-90)).tz(timezone).toDate(),
								)
							}
						>
							<Typography fontSize="small">3 m.</Typography>
						</IconButton>
						<IconButton
							size="small"
							sx={{ border: "1px solid", borderColor: colors.blueAccent[300], width: 50, height: 50, }}
							onClick={() =>
								props.SetStartDate(
									dayjs.utc(initialDate(-180)).tz(timezone).toDate(),
								)
							}
						>
							<Typography fontSize="small">6 m.</Typography>
						</IconButton>
						<IconButton
							size="small"
							sx={{ border: "1px solid", borderColor: colors.blueAccent[300], width: 50, height: 50, }}
							onClick={() =>
								props.SetStartDate(
									dayjs.utc(initialDate(-365)).tz(timezone).toDate(),
								)
							}
						>
							<Typography fontSize="small">12 m.</Typography>
						</IconButton>
						</Box>
					</Grid>
				</Grid>
		</Box>
	);
};
